import { useCallback, useState, useMemo, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, Tabs, TabList, Tab } from '@chakra-ui/react';
import TabListWrapper from '../utils/TabListWrapper';
import useHistoryPush from '../../hooks/useHistoryPush';
import useAutoformMetadata from 'components/autoform/hooks/useAutoformMetadata';
import useAutoformTableMetadata from 'components/autoform/hooks/useAutoformTableMetadata';

const TabularPage = ({ routes, isAutoForm = false }) => {
    let historyPush = useHistoryPush();
    const { pathname } = useLocation();
    const [selectedTab, setSelectedTab] = useState(0);
    const [persistedRoute, setPersistedRoute] = useState('');
    const { hierarchicalTables } = useAutoformMetadata();
    const level1TableId = hierarchicalTables?.level1[0]?.id;
    const { selectedItem, primaryKeyColumnName } = useAutoformTableMetadata(level1TableId);

    const getPathByRouteIndex = useCallback((index) => `${routes[index].relativePath}`, [routes]);

    const defaultTabIndex = useMemo(() => {
        let index = 0;
        for (let i = 0; i < routes.length; i++) {
            const pathByRouteIndex = getPathByRouteIndex(i);
            const route = pathname.split('/').slice(2);
            if (route.includes(pathByRouteIndex)) {
                index = i;
                break;
            }
        }

        return index;
    }, [routes, pathname, getPathByRouteIndex]);

    const updateRouteBySelected = useCallback(
        (route) => {
            if (selectedItem && primaryKeyColumnName === 'ITEMID') {
                setPersistedRoute(selectedItem);
                return `${route}/${selectedItem}`;
            }
            const persistedRouteUpdate = persistedRoute ? `${route}/${persistedRoute}` : `${route}`;

            return persistedRouteUpdate;
        },
        [primaryKeyColumnName, selectedItem, persistedRoute]
    );

    const onTabOpen = useCallback(
        (index) => {
            setSelectedTab(index);
            const relativePath = getPathByRouteIndex(index);
            if (isAutoForm) {
                historyPush(relativePath);
            } else {
                const updatedPath = updateRouteBySelected(relativePath);
                historyPush(updatedPath);
            }
        },
        [getPathByRouteIndex, historyPush, isAutoForm, updateRouteBySelected]
    );

    useEffect(() => {
        if (selectedTab !== defaultTabIndex) {
            setSelectedTab(defaultTabIndex);
        }
    }, [defaultTabIndex, selectedTab, setSelectedTab]);

    return (
        <>
            <Box bg="background-secondary" pt="73px">
                <Tabs isManual isLazy onChange={onTabOpen} index={selectedTab}>
                    <TabList>
                        <Box px={6} w="100%">
                            <TabListWrapper>
                                {routes.map((route) => (
                                    <Tab key={route.relativePath} flexShrink={0}>
                                        {route.name}
                                    </Tab>
                                ))}
                            </TabListWrapper>
                        </Box>
                    </TabList>
                </Tabs>
            </Box>
            <Outlet context={{ setPersistedRoute }} />
        </>
    );
};

export default TabularPage;
